import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getOnboardingRoute } from "@src/application/users/dto/auth-response.dto";

/**
 * PublicRouteGuard - Guards public routes for authenticated users with incomplete profiles
 * - Allows unauthenticated users to access public routes
 * - Allows authenticated users with complete profiles to access public routes
 * - Redirects authenticated users with incomplete profiles to onboarding
 */
export const PublicRouteGuard: React.FC = () => {
  const { isAuthenticated, isProfileComplete, profileState, initializing, loading } = useAuth();
  const location = useLocation();

  // Debug logging in development
  if (import.meta.env.DEV) {
    console.log('🔍 PublicRouteGuard:', {
      pathname: location.pathname,
      isAuthenticated,
      isProfileComplete,
      profileState,
      initializing,
      loading,
    });
  }

  // Don't redirect during initial load
  if (initializing || loading) {
    return <Outlet />;
  }

  // If authenticated but profile is incomplete, redirect to onboarding
  if (isAuthenticated && !isProfileComplete && profileState) {
    const onboardingRoute = getOnboardingRoute(profileState);
    
    if (import.meta.env.DEV) {
      console.log('🚨 PublicRouteGuard: Redirecting to onboarding:', onboardingRoute);
    }
    
    return <Navigate to={onboardingRoute} replace />;
  }

  // Allow access to public route
  return <Outlet />;
};
